import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import {
  CreditCard,
  FileText,
  QrCode,
  LogOut,
  AlertCircle,
  Loader2,
  Edit,
} from "lucide-react";

const SettingsMenu = () => {
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes logout-progress {
        0% { width: 0%; }
        100% { width: 100%; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [showLogoutSuccess, setShowLogoutSuccess] = useState(false);
  const navigate = useNavigate();
  const { user, logOut } = useUserAuth();

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const firestore = getFirestore();
        const docRef = doc(firestore, "Users", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const userData = docSnap.data();
          setUserInfo({
            name:
              userData.nombre && userData.apellido
                ? `${userData.nombre} ${userData.apellido}`
                : "Usuario",
            email: userData.email || user.email,
          });
        } else {
          setUserInfo({
            name: "Usuario",
            email: user.email,
          });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setErrorMessage("Error al cargar los datos del usuario.");
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [user]);

  const handleLogout = async () => {
    try {
      setShowLogoutSuccess(true);
      setTimeout(async () => {
        await logOut();
        navigate("/login");
      }, 1500);
    } catch (error) {
      console.error("Error logging out:", error);
      setErrorMessage("Error al cerrar sesión. Intenta de nuevo.");
      setShowLogoutSuccess(false);
    }
  };

  const MenuItem = ({ item }) => (
    <div
      className="flex items-center justify-between bg-[#F5EBDD] p-3 sm:p-4 border-2 border-[#FF0000] rounded-none cursor-pointer hover:bg-[#FFF8E1] transition-all active:scale-95"
      onClick={() => item.path && navigate(item.path)}
    >
      <div className="flex items-center gap-3 sm:gap-4 flex-1">
        <div className="p-2 bg-[#FF0000] rounded-none flex-shrink-0">
          {item.icon}
        </div>
        <div className="min-w-0">
          <div className="font-bold text-[#000000] uppercase tracking-wide text-sm sm:text-base truncate">
            {item.label}
          </div>
          <div className="text-xs sm:text-sm text-[#FF0000] truncate">
            {item.description}
          </div>
        </div>
      </div>
    </div>
  );

  const profileMenuItems = [
    {
      icon: <Edit className="w-5 h-5 text-[#F5EBDD]" />,
      label: "Editar Perfil",
      description: "Configura tu información personal",
      path: "/addprofile",
    },
    {
      icon: <CreditCard className="w-5 h-5 text-[#F5EBDD]" />,
      label: "Pedidos",
      description: "Consulta tus pedidos",
      path: "/orders",
    },
    {
      icon: <FileText className="w-5 h-5 text-[#F5EBDD]" />,
      label: "Facturas",
      description: "Genera tus facturas",
      path: "/facturacion",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFBC00] px-4">
        <div className="flex flex-col items-center gap-4 text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#FF0000]" />
          <p className="text-[#000000] font-bold uppercase tracking-wide text-sm sm:text-base">
            Cargando información...
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFBC00] px-4">
        <div className="bg-[#F5EBDD] border-2 border-[#FF0000] p-4 sm:p-6 rounded-none shadow-xl max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-[#FF0000]" />
          <h2 className="text-lg sm:text-xl font-bold text-[#FF0000] uppercase tracking-wide mb-2">
            Acceso Restringido
          </h2>
          <p className="text-[#000000] text-sm sm:text-base mb-4 sm:mb-6">
            Debes iniciar sesión para acceder a esta página
          </p>
          <button
            onClick={() => navigate("/login")}
            className="w-full bg-[#FF0000] text-[#F5EBDD] font-bold uppercase tracking-wide py-2 sm:py-3 px-4 rounded-none hover:bg-[#000000] hover:text-[#FFBC00] transition-all active:scale-95"
          >
            Iniciar Sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFBC00] text-[#000000] relative">
      <div className="max-w-md mx-auto px-4 sm:px-6 md:px-8 pt-16 sm:pt-20">
        <div className="bg-[#F5EBDD] border-2 border-[#FF0000] p-4 sm:p-6 rounded-none shadow-xl mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-[#FF0000] uppercase tracking-wide truncate">
                {userInfo?.name}
              </h1>
              <p className="text-[#000000] text-xs sm:text-sm truncate">
                {userInfo?.email}
              </p>
            </div>
            <div className="flex gap-2 sm:gap-3">
              <button
                onClick={() => navigate("/qr")}
                className="p-2 sm:p-3 bg-[#FF0000] text-[#F5EBDD] rounded-none hover:bg-[#000000] hover:text-[#FFBC00] transition-all active:scale-95"
                aria-label="Ver Código QR"
              >
                <QrCode className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 sm:p-3 bg-[#FF0000] text-[#F5EBDD] rounded-none hover:bg-[#000000] hover:text-[#FFBC00] transition-all active:scale-95"
                aria-label="Cerrar Sesión"
              >
                <LogOut className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
          </div>
        </div>
        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-base sm:text-lg font-bold text-[#FF0000] uppercase tracking-wide px-2 mb-3 sm:mb-4 border-l-4 border-[#FF0000]">
            Perfil
          </h2>
          {profileMenuItems.map((item, index) => (
            <MenuItem key={`profile-${index}`} item={item} />
          ))}
        </div>
      </div>
      <div className="text-center pt-6 pb-8 px-4">
        <p className="text-[#FFFFFF] text-xs font-bold uppercase tracking-wide">
          © {new Date().getFullYear()} Pollo Feliz
        </p>
      </div>
    </div>
  );
};

export default SettingsMenu;
